import { Button, Center, Spacer, Spinner, Stack } from '@chakra-ui/react';
import { useToast } from '@chakra-ui/react';
import { buildFlightPathString, copyText } from '@pelicargo/ui';
import { Show } from '@pelicargo/ui';
import { TimeZone } from '@pelicargo/utils';
import { HiOutlineClipboardCopy } from 'react-icons/hi';
import { HiPlus } from 'react-icons/hi2';

import { useCreateFlightSegment } from '../../hooks/flightSegment/useCreate';
import { useFlightSegments } from '../../hooks/flightSegment/useFlightSegments';
import { useRequest } from '../../hooks/request/useRequest';
import { FlightSegment, Quote } from '../../utils/types';
import { AiFilledFlightsNote } from '../AiFilledFlightsNote';
import { Column, FlightSegmentRow, Row } from './Row';

interface FlightSegmentsTableProps {
  quote: Quote;
}

const isClipboardApiSupported = () => {
  return !!navigator.clipboard && typeof navigator.clipboard.writeText === 'function';
};

export const FlightSegmentsTable = ({ quote }: FlightSegmentsTableProps) => {
  const { data: request } = useRequest(quote.request_id);
  const [flightSegments, isLoading] = useFlightSegments(quote?.id);

  const requestOriginTimezone = request?.origin_airports?.[0]?.time_zone as TimeZone;
  const requestDestinationTimezone = request?.destination_airports?.[0]?.time_zone as TimeZone;
  const toast = useToast();

  const { create, isLoading: isCreating } = useCreateFlightSegment(quote?.id);

  const handleFlightSegmentCreate = async () => {
    if (!quote?.id) return;

    const origin_airport_id = flightSegments?.[flightSegments.length - 1]?.destination_airport_id;
    const provider_id = quote?.provider_id;
    try {
      await create({ origin_airport_id, provider_id, flight_path_position: 0 });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
      });
    }
  };

  const renderRow = (fs: FlightSegment, idx: number) => (
    <FlightSegmentRow
      key={`fs-${fs.id}`}
      flightSegment={fs}
      prevDestination={flightSegments?.[idx - 1]?.destination_airport_id}
      nextOrigin={flightSegments?.[idx + 1]?.origin_airport_id}
      defaultOriginTimezone={requestOriginTimezone}
      defaultDestinationTimezone={requestDestinationTimezone}
    />
  );

  const handleClick = async () => {
    copyText(buildFlightPathString(flightSegments));
    if (
      flightSegments.some(
        (segment) =>
          !(
            segment.provider_id &&
            segment.flight_number &&
            segment.origin_airport_id &&
            segment.destination_airport_id &&
            segment.arrives_at &&
            segment.departs_at
          ),
      )
    ) {
      toast({
        title: 'The copied string may be malformed',
        description:
          'A flight segment is missing information. Each flight segment should have an airline, flight number, origin airport, destination airport, arrives at date, and departs at date',
        status: 'warning',
      });
    } else {
      toast({ title: 'Flight segments copied!', status: 'success' });
    }
  };

  return (
    <>
      <Show if={isLoading}>
        <Center w="full" py={4}>
          <Spinner size="sm" />
        </Center>
      </Show>
      <Show if={!isLoading}>
        <Row position="relative">
          <Column label="Departs" flex={2} />
          <Column label="Arrives" flex={2} borderRightWidth={0} />
          <Spacer flex={3} pl={2} pr={2} borderLeftWidth={1} borderColor="transparent" />
          <Button
            variant="ghost"
            leftIcon={<HiOutlineClipboardCopy />}
            onClick={handleClick}
            disabled={!isClipboardApiSupported}
            position="absolute"
            right="0"
          >
            Copy
          </Button>
        </Row>
      </Show>
      {flightSegments?.map?.(renderRow)}
      <AiFilledFlightsNote flightSegments={flightSegments} quoteId={quote?.id} />
      <Show if={!flightSegments.length}>
        <Stack direction="row" justifyContent="center" alignItems="center" py={4}>
          <Button
            alignSelf="center"
            variant="outline"
            size="sm"
            onClick={handleFlightSegmentCreate}
            isLoading={isCreating}
            leftIcon={<HiPlus />}
          >
            Add Flight Segment
          </Button>
        </Stack>
      </Show>
    </>
  );
};
